/**
 * CollaborativeDocumentEditor — Tiptap v3 + Yjs CRDT collaborative editor.
 *
 * Features:
 *   - Real-time collaboration via YjsWebPubSubProvider over Azure Web PubSub
 *   - Deterministic clientID for initial HTML to prevent content duplication
 *   - contentReady gate: editor is non-editable until initial content loads
 *   - Loading overlay with spinner while syncing
 *   - Auto-save with configurable interval
 *   - Rich text toolbar (headings, formatting, lists, tables, alignment, etc.)
 *   - Word count in status bar
 *   - Read-only mode with configurable reason message
 *   - Presence tracking with collaborator avatars
 *   - Expanded (fullscreen-like) mode support
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import Typography from '@tiptap/extension-typography';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import FontFamily from '@tiptap/extension-font-family';
import { Extension } from '@tiptap/core';
import Collaboration from '@tiptap/extension-collaboration';
import { yCursorPlugin } from '@tiptap/y-tiptap';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';

import { YjsWebPubSubProvider, getInitialContentClientID } from '../../services/YjsWebPubSubProvider';
import ProfileImage from '../Header/ProfileImage';
// @ts-ignore css import
import './CollaborativeDocumentEditor.css';

import {
  PiTextBLight as PiBoldIcon,
  PiTextItalicLight as PiItalicIcon,
  PiTextUnderlineLight as PiUnderlineIcon,
  PiTextStrikethroughLight as PiStrikethroughIcon,
  PiTextAlignLeftLight,
  PiTextAlignCenterLight,
  PiTextAlignRightLight,
  PiTextAlignJustifyLight,
  PiListBulletsLight,
  PiListNumbersLight,
  PiQuotesLight,
  PiCodeLight,
  PiMinusLight,
  PiTableLight,
  PiTextSuperscriptLight as PiSuperscriptIcon,
  PiTextSubscriptLight as PiSubscriptIcon,
  PiAtLight,
  PiArrowUUpLeftLight,
  PiArrowUUpRightLight,
  PiSpinnerGapLight,
  PiLock,
  PiLockLight,
} from 'react-icons/pi';
import Spinner from './TailwindComponents/Spinner';

// ─── Collaborator colors ─────────────────────────────────────────────

const COLLABORATOR_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
];

function getCollaboratorColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLLABORATOR_COLORS[Math.abs(hash) % COLLABORATOR_COLORS.length];
}

// ─── Props ───────────────────────────────────────────────────────────

interface CollaborativeDocumentEditorProps {
  /** Matter UUID — used for collaboration room */
  matterUuid: string;
  /** Document type ID (e.g. "affidavits_affidavit_of_service") */
  documentId: string;
  /** Initial HTML content from the backend */
  initialHtml: string;
  /** Current user info */
  user: {
    id: string;
    name: string;
    email?: string;
    profilePicture?: string;
  };
  /** Whether editor is read-only */
  readOnly?: boolean;
  /** Reason for read-only (shown in toolbar) */
  readOnlyReason?: string;
  /** Called on content change with (html, json) */
  onSave?: (html: string, json: any) => void;
  /** Auto-save debounce interval in ms */
  autoSaveInterval?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the editor is in expanded/fullscreen mode */
  isExpanded?: boolean;
  /** Callback for active users list changes */
  onActiveUsersChange?: (users: Array<{ id: string; name: string; color: string }>) => void;
}

// ─── Component ───────────────────────────────────────────────────────

const CollaborativeDocumentEditor: React.FC<CollaborativeDocumentEditorProps> = ({
  matterUuid,
  documentId,
  initialHtml,
  user,
  readOnly = false,
  readOnlyReason,
  onSave,
  autoSaveInterval = 3000,
  placeholder,
  isExpanded = false,
  onActiveUsersChange,
}) => {
  const { t } = useTranslation();
  const [connected, setConnected] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [contentReady, setContentReady] = useState(false);

  // Refs for cleanup and debounce
  const providerRef = useRef<YjsWebPubSubProvider | null>(null);
  const ydocRef = useRef<Y.Doc | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialHtmlRef = useRef(initialHtml);
  const mountedRef = useRef(true);

  // Update the ref when initialHtml changes (for re-renders)
  initialHtmlRef.current = initialHtml;

  // ── Create Y.Doc and provider (once per mount, in useEffect for StrictMode safety) ──

  const ydoc = useMemo(() => new Y.Doc(), []);
  ydocRef.current = ydoc;

  // Create awareness synchronously so CollaborationCursor has it at plugin init time
  const awareness = useMemo(() => {
    const a = new Awareness(ydoc);
    return a;
  }, [ydoc]);

  // Set local awareness state with user info — must be set before yCursorPlugin reads it.
  // IMPORTANT: Use setLocalState (not setLocalStateField) because after React
  // StrictMode double-mount cleanup, the awareness state may be null (deleted by
  // removeAwarenessStates in the previous provider's destroy). setLocalStateField
  // silently no-ops when getLocalState() is null.
  useEffect(() => {
    const color = getCollaboratorColor(user.id);
    const currentState = awareness.getLocalState() || {};
    awareness.setLocalState({
      ...currentState,
      user: {
        id: user.id,
        name: user.name,
        color,
        email: user.email || '',
        profilePicture: user.profilePicture || '',
      },
    });
  }, [awareness, user.id, user.name, user.email, user.profilePicture]);

  // ── Track awareness state → propagate active users to parent ──────

  useEffect(() => {
    if (!onActiveUsersChange) return;

    const updateUsers = () => {
      const users: Array<{ id: string; name: string; color: string; email?: string; profilePicture?: string }> = [];
      awareness.getStates().forEach((state, clientId) => {
        // Skip self
        if (clientId === ydoc.clientID) return;
        const u = state.user;
        if (u && u.name) {
          users.push({
            id: u.id || String(clientId),
            name: u.name,
            color: u.color || '#6b7280',
            email: u.email,
            profilePicture: u.profilePicture,
          });
        }
      });
      onActiveUsersChange(users);
    };

    awareness.on('change', updateUsers);
    // Fire once immediately
    updateUsers();

    return () => {
      awareness.off('change', updateUsers);
    };
  }, [awareness, ydoc.clientID, onActiveUsersChange]);

  // ── Tiptap Editor ─────────────────────────────────────────────────

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // @ts-ignore — Yjs handles undo/redo; disable built-in history
        history: false,
      }),
      Placeholder.configure({ placeholder: placeholder ?? t('Start typing…') }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Superscript,
      Subscript,
      Typography,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList,
      TaskItem.configure({ nested: true }),
      FontFamily,
      Collaboration.configure({
        document: ydoc,
        field: 'default',
      }),
      // Custom extension: use yCursorPlugin from @tiptap/y-tiptap (same package
      // as ySyncPlugin) so both share the same ySyncPluginKey instance.
      // Using @tiptap/extension-collaboration-cursor would import yCursorPlugin
      // from y-prosemirror, which creates a SEPARATE ySyncPluginKey — causing
      // "Cannot read properties of undefined (reading 'doc')" at createDecorations.
      Extension.create({
        name: 'collaborationCursor',
        addProseMirrorPlugins() {
          return [yCursorPlugin(awareness)];
        },
      }),
    ],
    editable: !readOnly && contentReady,
    onUpdate: ({ editor: ed }) => {
      // Compute word count
      const text = ed.state.doc.textContent;
      setWordCount(text.split(/\s+/).filter(Boolean).length);

      // Debounced auto-save
      if (!readOnly && onSave) {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
          const html = ed.getHTML();
          const json = ed.getJSON();
          onSave(html, json);
        }, autoSaveInterval);
      }
    },
  });

  // ── Sync editable state ───────────────────────────────────────────

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly && contentReady);
    }
  }, [editor, readOnly, contentReady]);

  // ── Load initial content into Y.Doc and connect provider ──────────

  useEffect(() => {
    if (!editor || !ydoc) return;
    mountedRef.current = true;

    const color = getCollaboratorColor(user.id);
    const provider = new YjsWebPubSubProvider({
      doc: ydoc,
      matterId: matterUuid,
      documentId,
      awareness, // Share the same awareness instance with CollaborationCursor
      user: {
        id: user.id,
        name: user.name,
        color,
        email: user.email || '',
        profilePicture: user.profilePicture || '',
      },
    });
    providerRef.current = provider;

    // Connection status callback
    provider.onConnectionChange((isConnected) => {
      if (mountedRef.current) setConnected(isConnected);
    });

    // Synced callback — when sync step 2 arrives (or timeout)
    provider.onSyncedChange((isSynced) => {
      if (!mountedRef.current) return;

      // Check if existing Yjs doc has content from peers
      const fragment = ydoc.getXmlFragment('default');
      const existingContent = fragment.toJSON();
      const hasRemoteContent = existingContent && existingContent.length > 0 &&
        !(existingContent.length === 1 && existingContent[0] === '<paragraph></paragraph>');

      if (hasRemoteContent) {
        // Peers sent us content — use it (don't load initialHtml)
        console.log('[CollaborativeDocumentEditor] Using synced content from peers');
        // Compute word count from synced content
        const text = editor.state.doc.textContent;
        setWordCount(text.split(/\s+/).filter(Boolean).length);
      } else {
        // No remote content — load initialHtml with deterministic clientID
        console.log('[CollaborativeDocumentEditor] No remote content, loading initialHtml');
        const savedClientID = ydoc.clientID;
        ydoc.clientID = getInitialContentClientID();
        try {
          editor.commands.setContent(initialHtmlRef.current, { emitUpdate: false });
          const text = editor.state.doc.textContent;
          setWordCount(text.split(/\s+/).filter(Boolean).length);
        } finally {
          ydoc.clientID = savedClientID;
        }
      }

      if (mountedRef.current) setContentReady(true);
    });

    // Connect the provider
    provider.connect();

    return () => {
      mountedRef.current = false;
      setContentReady(false);
      setConnected(false);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      provider.destroy();
      providerRef.current = null;
    };
  }, [editor, ydoc, matterUuid, documentId, user.id, user.name, user.email, user.profilePicture]);

  // ── Toolbar helpers ───────────────────────────────────────────────

  const ToolbarButton: React.FC<{
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
  }> = useCallback(({ onClick, active, disabled, title, children }) => (
    <button
      onClick={onClick}
      className={active ? 'is-active' : ''}
      disabled={disabled || readOnly || !contentReady}
      title={title}
      type="button"
    >
      {children}
    </button>
  ), [readOnly, contentReady]);

  // ── Render ────────────────────────────────────────────────────────

  if (!editor) return null;

  return (
    <div className={`document-editor-page ${isExpanded ? 'expanded' : ''}`}>
      {/* Loading overlay — shown while connecting/syncing */}
      {!contentReady && !readOnly && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-bg/80 backdrop-blur-[2px]">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="glyph" className="!h-6 !w-6" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {connected ? t('Syncing document...') : t('Connecting...')}
            </span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="document-editor-toolbar">
        {readOnly ? (
          <div className="flex items-center gap-2 px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
            <PiLockLight className="w-4 h-4" />
            <span>{readOnlyReason ? `Read-only — ${readOnlyReason}` : 'Read-only mode'}</span>
          </div>
        ) : (
          <>
            {/* Heading select */}
            <select
              className="toolbar-select"
              value={
                editor.isActive('heading', { level: 1 }) ? 'h1' :
                editor.isActive('heading', { level: 2 }) ? 'h2' :
                editor.isActive('heading', { level: 3 }) ? 'h3' :
                editor.isActive('heading', { level: 4 }) ? 'h4' :
                'paragraph'
              }
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'paragraph') {
                  editor.chain().focus().setParagraph().run();
                } else {
                  const level = parseInt(val.replace('h', '')) as 1 | 2 | 3 | 4;
                  editor.chain().focus().toggleHeading({ level }).run();
                }
              }}
              disabled={!contentReady}
            >
              <option value="paragraph">{t('Normal text')}</option>
              <option value="h1">{t('Heading 1')}</option>
              <option value="h2">{t('Heading 2')}</option>
              <option value="h3">{t('Heading 3')}</option>
              <option value="h4">{t('Heading 4')}</option>
            </select>

            <div className="toolbar-divider" />

            {/* Formatting */}
            <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title={t('Bold')}>
              <PiBoldIcon className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title={t('Italic')}>
              <PiItalicIcon className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title={t('Underline')}>
              <PiUnderlineIcon className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title={t('Strikethrough')}>
              <PiStrikethroughIcon className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarButton onClick={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive('superscript')} title={t('Superscript')}>
              <PiSuperscriptIcon className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive('subscript')} title={t('Subscript')}>
              <PiSubscriptIcon className="w-4 h-4" />
            </ToolbarButton>

            <div className="toolbar-divider" />

            {/* Alignment */}
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title={t('Align left')}>
              <PiTextAlignLeftLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title={t('Align center')}>
              <PiTextAlignCenterLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title={t('Align right')}>
              <PiTextAlignRightLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title={t('Justify')}>
              <PiTextAlignJustifyLight className="w-4 h-4" />
            </ToolbarButton>

            <div className="toolbar-divider" />

            {/* Lists */}
            <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title={t('Bullet list')}>
              <PiListBulletsLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title={t('Numbered list')}>
              <PiListNumbersLight className="w-4 h-4" />
            </ToolbarButton>

            <div className="toolbar-divider" />

            {/* Block elements */}
            <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title={t('Blockquote')}>
              <PiQuotesLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title={t('Code block')}>
              <PiCodeLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title={t('Horizontal rule')}>
              <PiMinusLight className="w-4 h-4" />
            </ToolbarButton>

            {/* Table */}
            <ToolbarButton onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title={t('Insert table')}>
              <PiTableLight className="w-4 h-4" />
            </ToolbarButton>

            <div className="toolbar-divider" />

            {/* Undo / Redo */}
            <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title={t('Undo')}>
              <PiArrowUUpLeftLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title={t('Redo')}>
              <PiArrowUUpRightLight className="w-4 h-4" />
            </ToolbarButton>
          </>
        )}
      </div>

      {/* Editor content */}
      <div className="document-editor-content">
        <EditorContent editor={editor} />
      </div>

      {/* Status bar */}
      <div className="document-editor-statusbar">
        <span className={`flex items-center gap-1.5 ${connected ? 'text-success-600 dark:text-success-400' : 'text-gray-400'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-success-500' : 'bg-gray-400'}`} />
          {connected ? t('Connected') : t('Disconnected')}
        </span>

        <span>{t('{{count}} words', { count: wordCount })}</span>

        {onSave && !readOnly && (
          <span className="text-success-600 dark:text-success-400">{t('✓ Saved')}</span>
        )}
      </div>
    </div>
  );
};

export default CollaborativeDocumentEditor;
